import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import {
  cleanNumber,
  ExportColumn,
  formatDate,
  isNumeric,
  type ColumnMeta,
} from "../Utils/anomaliesColumns";

interface Props {
  metadata: Record<string, ColumnMeta>;
  rows: any[];
}

// Column subset + order + labels follow the spec (NOT output_metadata).
// `key` is the real API field; `label` is the header. Exported so the parent
// can drive the CSV export from the exact same list (key + label + format).
export const DAILY_EARNING_GAPS_COLUMNS: ExportColumn[] = [
  { key: "brand_name", label: "Brand Name" },
  { key: "brand_id", label: "Brand ID" },
  {
    key: "brand_action_date",
    label: "Brand Action Date",
    format: (v) => formatDate(v),
  },
  { key: "gap", label: "Earning Gap", format: (v) => formatCurrency(v) },
  {
    key: "past_totalincome",
    label: "Previous Total Income",
    format: (v) => formatCurrency(v),
  },
  {
    key: "now_totalincome",
    label: "Current Total Income",
    format: (v) => formatCurrency(v),
  },
  {
    key: "captured_date",
    label: "Data Capture Date",
    format: (v) => formatDate(v),
  },
];

// Money columns get "$" + thousand separators.
const CURRENCY_KEYS = new Set(["gap", "past_totalincome", "now_totalincome"]);

// Full dates rendered as DD/MM/YYYY and sorted chronologically.
const DATE_KEYS = new Set(["brand_action_date", "captured_date"]);

// Earning Gap is highlighted red when abs(value) > 500.
const isHighlighted = (key: string, value: any) => {
  if (!isNumeric(value)) return false;
  if (key === "gap") return Math.abs(cleanNumber(value)) > 500;
  return false;
};

// e.g. 1055 -> "$1,055", -1055 -> "-$1,055", 0 -> "$0"
const formatCurrency = (value: any) => {
  if (!isNumeric(value)) return String(value);
  const num = cleanNumber(value);
  const sign = num < 0 ? "-" : "";
  const abs = Math.abs(num).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
  return `${sign}$${abs}`;
};

const DailyEarningGapsTable = ({ metadata, rows }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "desc" });

  // Fixed spec columns, kept only if present in the API metadata. The header
  // still renders when query_output is empty (metadata is still returned).
  const columns = React.useMemo(() => {
    if (!metadata) return [];
    return DAILY_EARNING_GAPS_COLUMNS.filter((c) => metadata[c.key]);
  }, [metadata]);

  // Default sort: Earning Gap, descending (falls back to first column).
  React.useEffect(() => {
    if (columns.length > 0 && !sort.keytoSort) {
      const defaultKey =
        columns.find((c) => c.key === "gap")?.key ?? columns[0].key;
      setSort({ keytoSort: defaultKey, direction: "desc" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  React.useEffect(() => {
    setPage(0);
  }, [rows]);

  const handleSort = (key: string) => {
    setSort((prev) => ({
      keytoSort: key,
      direction:
        prev.keytoSort === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return rows;

    return [...rows].sort((a, b) => {
      const aRaw = a[sort.keytoSort];
      const bRaw = b[sort.keytoSort];
      if (aRaw == null && bRaw == null) return 0;
      if (aRaw == null) return 1;
      if (bRaw == null) return -1;

      if (DATE_KEYS.has(sort.keytoSort)) {
        const diff = dayjs(aRaw).valueOf() - dayjs(bRaw).valueOf();
        return sort.direction === "asc" ? diff : -diff;
      }

      if (!isNumeric(aRaw) && !isNumeric(bRaw)) {
        return sort.direction === "asc"
          ? String(aRaw).localeCompare(String(bRaw))
          : String(bRaw).localeCompare(String(aRaw));
      }
      const aNum = cleanNumber(aRaw);
      const bNum = cleanNumber(bRaw);
      return sort.direction === "asc" ? aNum - bNum : bNum - aNum;
    });
  }, [rows, sort]);

  const renderCell = (key: string, row: any) => {
    const value = row[key];
    if (value == null || value === "") return "";

    let display: string;
    if (DATE_KEYS.has(key)) {
      const d = dayjs(value);
      display = d.isValid() ? d.format("DD/MM/YYYY") : String(value);
    } else if (CURRENCY_KEYS.has(key)) {
      display = formatCurrency(value);
    } else {
      display = String(value);
    }

    if (isHighlighted(key, value)) {
      return (
        <Box
          component="span"
          sx={{
            color: "#D32F2F",
            backgroundColor: "#FDEDED",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            display: "inline-block",
          }}
        >
          {display}
        </Box>
      );
    }
    return display;
  };

  return (
    <>
      <TableContainer>
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align="left"
                  sortDirection={
                    sort.keytoSort === col.key ? sort.direction : false
                  }
                >
                  <TableSortLabel
                    active={sort.keytoSort === col.key}
                    direction={
                      sort.keytoSort === col.key ? sort.direction : "desc"
                    }
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows?.length > 0 &&
              sortedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow
                    key={`${row.brand_id ?? "row"}-${rowIndex}`}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.key} align="left">
                        {renderCell(col.key, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            {sortedRows?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length || 1}
                  align="center"
                  sx={{ py: 3 }}
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {sortedRows?.length > 0 && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginLeft: "15px",
              },
            }}
          />
          <Pagination
            count={Math.ceil((rows?.length ?? 0) / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingRight: "16px",
              },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default DailyEarningGapsTable;
